import * as L from 'leaflet';
import { MapObjectReference } from './map-object';

/** One labelled fact in a map-object popover. Empty values are not rendered. */
export interface MapObjectPopoverValue {
  readonly label: string;
  readonly value: unknown;
}

/** A business record related to the primary map object. */
export interface MapObjectPopoverSection {
  readonly type: string;
  readonly title: string;
  readonly values?: readonly MapObjectPopoverValue[];

  /**
   * Which business record this is, when the popover is allowed to hand it
   * back. A section that carries one is drawn as a button, and clicking it
   * asks for that record wherever the page is showing its rows.
   *
   * Absent means the record is shown and not offered: a popover opened with no
   * handler draws every section the way it always did.
   */
  readonly reference?: MapObjectReference;
}

/** Asked for one record of a popover, by the page that opened the map. */
export type MapObjectPopoverSelect = (reference: MapObjectReference) => void;

/** Presentation model for a map object or an aggregate such as a cluster. */
export interface MapObjectPopover {
  readonly type: string;
  readonly title: string;
  readonly subtitle?: string;
  readonly values?: readonly MapObjectPopoverValue[];
  readonly sections?: readonly MapObjectPopoverSection[];
}

const POPOVER_OPTIONS: L.PopupOptions = {
  minWidth: 272,
  maxWidth: 360,
};

/** Opens a popover anchored to a marker, polygon, or path layer. */
export function bindLayerPopover(
  layer: L.Layer,
  model: MapObjectPopover,
  onSelect?: MapObjectPopoverSelect,
): void {
  layer.bindPopup(renderMapObjectPopover(model, onSelect), POPOVER_OPTIONS);
}

/**
 * Prepares a layer popover from its click handler.
 *
 * The first click binds and opens it. Subsequent clicks are handled only by
 * Leaflet's bound popup handler; this function just refreshes the content.
 * For client clusters that native handler runs before `clusterclick`, so
 * toggling here as well would immediately undo the native action.
 */
export function openLayerPopover(
  layer: L.Layer,
  model: MapObjectPopover,
  onSelect?: MapObjectPopoverSelect,
): void {
  if (layer.getPopup()) {
    layer.setPopupContent(renderMapObjectPopover(model, onSelect));
  } else {
    bindLayerPopover(layer, model, onSelect);
    layer.openPopup();
  }
}

/** Opens a popover for a virtual object, such as a canvas-rendered grid cell. */
export function openMapPopover(
  map: L.Map,
  at: L.LatLngExpression,
  model: MapObjectPopover,
  options?: L.PopupOptions,
  onSelect?: MapObjectPopoverSelect,
): L.Popup {
  return L.popup({ ...POPOVER_OPTIONS, ...options })
    .setLatLng(at)
    .setContent(renderMapObjectPopover(model, onSelect))
    .openOn(map);
}

/**
 * Renders text through DOM nodes so business values cannot become markup.
 *
 * A related record is drawn as a button when it carries a reference AND the
 * caller passed a handler. Both, deliberately: a popover nobody is listening
 * to must not offer a click that goes nowhere, and a section with no identity
 * has nothing to hand back.
 */
export function renderMapObjectPopover(
  model: MapObjectPopover,
  onSelect?: MapObjectPopoverSelect,
): HTMLElement {
  const root = element('div', 'map-object-popover');
  const primary = element('section', 'map-object-popover__primary');
  primary.append(
    element('div', 'map-object-popover__record-type', model.type),
    element('h3', 'map-object-popover__title', model.title),
  );
  if (model.subtitle) {
    primary.append(element(
      'div',
      'map-object-popover__subtitle',
      model.subtitle,
    ));
  }
  appendValues(primary, model.values);
  root.append(primary);

  const sections = model.sections ?? [];
  if (sections.length) {
    const related = element('section', 'map-object-popover__related');
    for (const section of sections) {
      const offered = section.reference && onSelect ? section.reference : null;
      const record: HTMLElement = offered
        ? element(
          'button',
          'map-object-popover__record map-object-popover__record--selectable')
        : element('article', 'map-object-popover__record');
      if (offered) {
        (record as HTMLButtonElement).type = 'button';
        record.title = `Show this ${section.type.toLowerCase()} in the list`;
        record.addEventListener('click', () => onSelect?.(offered));
      }
      record.append(
        element('div', 'map-object-popover__record-type', section.type),
        element('div', 'map-object-popover__record-title', section.title),
      );
      appendValues(record, section.values);
      related.append(record);
    }
    root.append(related);
  }

  return root;
}

function appendValues(
  parent: HTMLElement,
  values: readonly MapObjectPopoverValue[] | undefined,
): void {
  for (const field of values ?? []) {
    if (field.value === null || field.value === undefined || field.value === '') {
      continue;
    }
    parent.append(element(
      'div',
      'map-object-popover__value',
      element('span', 'map-object-popover__label', `${field.label}:`),
      ` ${formatValue(field.value)}`,
    ));
  }
}

function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  ...children: readonly (Node | string)[]
): HTMLElementTagNameMap[K] {
  const result = document.createElement(tag);
  result.className = className;
  result.append(...children);
  return result;
}

function formatValue(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toLocaleString();
  return String(value);
}
